import { useMemo } from "react";
import dayjs from "dayjs";
import { GetTask } from "@/services/fhir/task/GetTask";
import { isQuestionnaireTask } from "src-next/modules/questionnaire-builder/questionnaire-tasks/helpers/questionnaireTaskHelpers";
import type { ClinicalInsightDateRange } from "src-next/modules/clinical-insights/types/analysisTypes";
import type { LoadPhase } from "src-next/modules/clinical-insights/loadPhase";
import {
  deriveInsightLoadPhase,
  useCachedInsightPayload,
  useReportInsightLoadPhase,
} from "src-next/modules/clinical-insights/hooks/useCachedInsightPayload";
import { useInsightPatientHydration } from "src-next/modules/clinical-insights/hooks/useInsightPatientHydration";

export interface QuestionnaireSendData {
  questionnaireId: string;
  title: string;
  status: "requested" | "completed";
  practitioner: string;
  date: string;
  /** `Task.authoredOn` em ISO, para localizar a resposta no diálogo. */
  authoredOn: string;
  patientId: string;
}

interface QuestionnaireTasksPayload {
  tasks: fhir5.Task[];
}

interface UseQuestionnaireAnalysisDataArgs {
  owner: string;
  organizationKey: string;
  practitionerFilter: string;
  dateRange?: ClinicalInsightDateRange;
  getRequesterName: (requesterReference: string) => string;
  onLoadPhaseChange?: (phase: LoadPhase) => void;
  fetchEnabled?: boolean;
  isActive?: boolean;
}

function logicalQuestionnaireId(reference?: string): string | null {
  if (!reference?.startsWith("Questionnaire/")) return null;
  const id = reference.slice("Questionnaire/".length).split("/")[0]?.trim();
  return id || null;
}

function patientIdFromTask(task: fhir5.Task): string | null {
  const reference = task.for?.reference?.trim();
  if (!reference?.startsWith("Patient/")) return null;
  const id = reference.slice("Patient/".length).split("/")[0]?.trim();
  return id || null;
}

function requesterMatchesPractitioner(
  reference: string | undefined,
  practitionerId: string,
): boolean {
  if (!practitionerId || practitionerId === "all") return true;
  if (!reference?.startsWith("PractitionerRole/")) return false;
  const roleId = reference.slice("PractitionerRole/".length);
  return roleId === practitionerId || roleId.endsWith(`-${practitionerId}`);
}

function isAnswerStatus(status: fhir5.Task["status"]): status is "requested" | "completed" {
  return status === "requested" || status === "completed";
}

function isWithinAuthoredPeriod(
  authoredOn: string | undefined,
  dateRange?: ClinicalInsightDateRange,
): boolean {
  if (!authoredOn) return false;
  const authored = dayjs(authoredOn);
  if (!authored.isValid()) return false;
  if (dateRange?.startDate && authored.isBefore(dayjs(dateRange.startDate).startOf("day"))) {
    return false;
  }
  if (dateRange?.endDate && authored.isAfter(dayjs(dateRange.endDate).endOf("day"))) {
    return false;
  }
  return true;
}

function formatDatePt(value?: string): string {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : value;
}

export function useQuestionnaireAnalysisData({
  owner,
  organizationKey,
  practitionerFilter,
  dateRange,
  getRequesterName,
  onLoadPhaseChange,
  fetchEnabled = true,
  isActive = true,
}: UseQuestionnaireAnalysisDataArgs) {
  const enabled = fetchEnabled && Boolean(owner);

  const { data, isLoading, error, refresh } = useCachedInsightPayload<QuestionnaireTasksPayload>({
    analysisId: "questionarios",
    organizationId: organizationKey,
    dateRange,
    enabled,
    fetcher: async () => {
      const response = await GetTask({
        owner,
        sort: "-authored-on",
        fetchAllPages: true,
      });
      const tasks = (response?.entry ?? [])
        .map((entry: { resource?: fhir5.Task }) => entry?.resource)
        .filter((task: fhir5.Task | undefined): task is fhir5.Task => isQuestionnaireTask(task))
        .filter((task: fhir5.Task) => isAnswerStatus(task.status))
        .filter((task: fhir5.Task) => isWithinAuthoredPeriod(task.authoredOn, dateRange))
        .filter((task: fhir5.Task) => logicalQuestionnaireId(task.focus?.reference) != null)
        .filter((task: fhir5.Task) => patientIdFromTask(task) != null);
      return { tasks };
    },
  });

  const sends = useMemo(() => {
    if (!data) return [];
    const sorted = [...data.tasks]
      .filter((task) => requesterMatchesPractitioner(task.requester?.reference, practitionerFilter))
      .sort((a, b) => (b.authoredOn ?? "").localeCompare(a.authoredOn ?? ""));

    const titles = new Map<string, string>();
    for (const task of sorted) {
      const questionnaireId = logicalQuestionnaireId(task.focus?.reference);
      if (!questionnaireId || titles.has(questionnaireId)) continue;
      const description = task.description?.trim();
      if (description) titles.set(questionnaireId, description);
    }

    const rows: QuestionnaireSendData[] = [];
    for (const task of sorted) {
      const questionnaireId = logicalQuestionnaireId(task.focus?.reference);
      const patientId = patientIdFromTask(task);
      if (!questionnaireId || !patientId || !isAnswerStatus(task.status)) continue;
      rows.push({
        questionnaireId,
        title: titles.get(questionnaireId) || task.description?.trim() || questionnaireId,
        status: task.status,
        practitioner: getRequesterName(task.requester?.reference ?? ""),
        date: formatDatePt(task.authoredOn),
        authoredOn: task.authoredOn ?? "",
        patientId,
      });
    }
    return rows;
  }, [data, getRequesterName, practitionerFilter]);

  const patientIds = useMemo(
    () => (data ? sends.map((send) => send.patientId) : []),
    [data, sends],
  );

  const hydration = useInsightPatientHydration(patientIds, Boolean(data));

  const loadPhase = deriveInsightLoadPhase({
    enabled,
    isLoading,
    error,
    hasPayload: data !== undefined,
    patientsLoading: hydration.isLoading,
  });

  useReportInsightLoadPhase(isActive, loadPhase, onLoadPhaseChange);

  return {
    sends,
    patients: hydration.patientMap,
    loadPhase,
    patientsProgress: hydration.progress,
    hasData: data !== undefined,
    retry: () => {
      void refresh();
    },
  };
}
